// Layout público de reservas — hero estilo perfil de agenda
import { css } from "styled-system/css";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { FadeIn } from "@/components/ui/animated";
import { parsePublicProfileThemeId, publicProfileThemeStyleVars } from "@/lib/public-profile-theme-vars";

type Props = {
  slug: string;
  name: string;
  category: string | null;
  specialty?: string | null;
  avatarUrl: string | null;
  primaryColor: string;
  profileTheme: string;
  /** Texto plano de bienvenida (HTML/bio resumido desde la página) */
  welcomeSubtitle: string;
  children: React.ReactNode;
};

export function normalizePrimary(hex: string): string {
  return hex?.trim().match(/^#([0-9A-Fa-f]{6})$/) ? hex.trim() : "#4f46e5";
}

export function BookPublicLayout({
  slug,
  name,
  category,
  specialty,
  avatarUrl,
  primaryColor,
  profileTheme,
  welcomeSubtitle,
  children,
}: Props) {
  const themeId = parsePublicProfileThemeId(profileTheme);
  const themeVars = publicProfileThemeStyleVars(themeId);
  const accent = normalizePrimary(primaryColor);

  const pageStyle: CSSProperties = {
    ...themeVars,
    backgroundColor: "var(--pp-page-bg)",
    color: "var(--pp-text)",
  };

  const bannerBg: CSSProperties = {
    background: `linear-gradient(165deg, color-mix(in srgb, ${accent} 78%, white) 0%, color-mix(in srgb, ${accent} 35%, #94a3b8) 100%)`,
    backgroundBlendMode: "normal",
  };

  const bannerPattern: CSSProperties = {
    backgroundImage: `repeating-linear-gradient(
      -32deg,
      transparent,
      transparent 14px,
      rgba(255, 255, 255, 0.07) 14px,
      rgba(255, 255, 255, 0.07) 28px
    )`,
  };

  const shell = css({
    w: "100%",
    maxW: "920px",
    mx: "auto",
    px: { base: "4", sm: "6" },
    pb: "10",
  });

  const manageLink = css({
    fontSize: "sm",
    fontWeight: "semibold",
    color: accent,
    textDecoration: "none",
    _hover: { opacity: 0.88 },
  });

  return (
    <div className={css({ minH: "100vh", display: "flex", flexDir: "column" })} style={pageStyle}>
      {/* Mobile: barra compacta */}
      <div
        className={css({
          display: { base: "flex", lg: "none" },
          alignItems: "center",
          gap: "3",
          p: "3",
          borderBottomWidth: "1px",
          borderColor: "var(--pp-border)",
          bg: "var(--pp-card-bg)",
        })}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" width={40} height={40} className={css({ rounded: "full", objectFit: "cover" })} />
        ) : (
          <div className={css({ w: "10", h: "10", rounded: "full", bg: "var(--pp-border)" })} />
        )}
        <div className={css({ flex: "1", minW: 0 })}>
          <p className={css({ fontWeight: "bold", fontSize: "sm", truncate: true })}>{name}</p>
          {category ? <p className={css({ fontSize: "xs", color: "var(--pp-muted)", truncate: true })}>{category}</p> : null}
        </div>
        <Link to={`/book/${slug}/manage`} className={css({ fontSize: "xs", fontWeight: "semibold", flexShrink: 0 })} style={{ color: accent }}>
          Gestionar
        </Link>
      </div>

      <div className={shell}>
        <div className={css({ display: { base: "none", lg: "block" } })}>
          <div
            className={css({
              position: "relative",
              h: { lg: "36" },
              roundedBottom: "2xl",
              overflow: "hidden",
            })}
            style={bannerBg}
          >
            <div className={css({ position: "absolute", inset: "0" })} style={bannerPattern} aria-hidden />
          </div>

          <div className={css({ display: "flex", flexDir: "column", alignItems: "center", mt: "-14", position: "relative", zIndex: 1 })}>
            <div
              className={css({
                w: "28",
                h: "28",
                rounded: "full",
                borderWidth: "4px",
                borderColor: "white",
                borderStyle: "solid",
                overflow: "hidden",
                flexShrink: 0,
                boxShadow: "0 10px 28px rgba(15, 23, 42, 0.12)",
                bg: "var(--pp-card-bg)",
              })}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" width={112} height={112} className={css({ w: "full", h: "full", objectFit: "cover" })} />
              ) : null}
            </div>
            <h1
              className={css({
                mt: "4",
                fontSize: "2xl",
                fontWeight: "bold",
                color: "var(--pp-text)",
                textAlign: "center",
                letterSpacing: "-0.02em",
              })}
            >
              {name}
            </h1>
            {category ? (
              <p className={css({ fontSize: "sm", color: "var(--pp-muted)", textAlign: "center", mt: "1" })}>{category}</p>
            ) : null}
            {specialty ? (
              <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "var(--pp-muted)", textAlign: "center", mt: "1", letterSpacing: "0.04em", textTransform: "uppercase" })}>{specialty}</p>
            ) : null}
            <p
              className={css({
                mt: "3",
                fontSize: "sm",
                color: "var(--pp-muted)",
                textAlign: "center",
                maxW: "lg",
                lineHeight: "1.55",
                px: "2",
              })}
            >
              {welcomeSubtitle}
            </p>
            <div className={css({ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "4", mt: "4" })}>
              <Link to={`/book/${slug}/manage`} className={manageLink}>
                Gestionar mi reserva
              </Link>
              <span className={css({ color: "var(--pp-border)", display: { base: "none", sm: "inline" } })} aria-hidden>
                |
              </span>
              <Link
                to={`/book/${slug}/manage`}
                className={css({
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2",
                  fontSize: "sm",
                  fontWeight: "semibold",
                  color: "var(--pp-muted)",
                  textDecoration: "none",
                  _hover: { color: "var(--pp-text)" },
                })}
              >
                <CalendarClock size={16} aria-hidden />
                Reprogramar / Cancelar
              </Link>
            </div>
          </div>
        </div>

        {/* Hero compacto en mobile (sin banner ancho) */}
        <div className={css({ display: { base: "block", lg: "none" }, pt: "6", textAlign: "center" })}>
          <div className={css({ display: "flex", justifyContent: "center" })}>
            <div
              className={css({
                w: "24",
                h: "24",
                rounded: "full",
                borderWidth: "3px",
                borderColor: "var(--pp-card-bg)",
                borderStyle: "solid",
                overflow: "hidden",
                boxShadow: "md",
                bg: "var(--pp-border)",
              })}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" width={96} height={96} className={css({ w: "full", h: "full", objectFit: "cover" })} />
              ) : null}
            </div>
          </div>
          <h1 className={css({ mt: "3", fontSize: "xl", fontWeight: "bold", color: "var(--pp-text)" })}>{name}</h1>
          {category ? <p className={css({ fontSize: "sm", color: "var(--pp-muted)", mt: "1" })}>{category}</p> : null}
          {specialty ? (
            <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "var(--pp-muted)", mt: "0.5", letterSpacing: "0.04em", textTransform: "uppercase" })}>{specialty}</p>
          ) : null}
          <p className={css({ mt: "3", fontSize: "sm", color: "var(--pp-muted)", lineHeight: "1.5", px: "1" })}>{welcomeSubtitle}</p>
        </div>

        <FadeIn delay={0.06} y={10}>
          {children}
        </FadeIn>

        <p
          className={css({
            mt: "12",
            mb: "6",
            textAlign: "center",
            fontSize: "xs",
            color: "var(--pp-muted)",
            letterSpacing: "0.04em",
          })}
        >
          Powered by SFTURNOS
        </p>
      </div>
    </div>
  );
}
